import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { CvlApplicationStatusEnumType, CvlPaymentStatusEnumType } from '__generated__/graphql';
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanel from 'components/FilterPanel';

import debounce from 'utils/debounce';
import cvlApplicationStatusToReadable from 'utils/enums/cvl-application-status-to-readable';
import cvlPaymentStatusToReadable from 'utils/enums/cvl-payment-status-to-readable';
import useScroll from 'utils/hooks/use-scroll';

const INVESTOR_ACCREDITATION_APPLICATIONS_QUERY = gql(`
  query getInvestorAccreditationApplications($cursor: ID, $limit: Int, $filters: InvestorAccreditationApplicationsFilterType) {
    investorAccreditationApplications(cursor: $cursor, limit: $limit, filters: $filters) {
      nodes {
        id
        investmentEntityId
        name
        pan
        email
        mobile
        status
        paymentStatus
        validityPeriodOfAccreditation
        financialDetails {
          qualificationCriteria
        }
        createdAt
      }

      pageInfo {
        hasNextPage
        cursor
        totalCount
      }
    }
  }
`);

const AccreditationApplication: FC = () => {
  const navigate = useNavigate();
  const { onScroll, onComponentUnmount, onDataLoad, onReset } = useScroll({
    pageIdentifier: 'accreditationApplicationPageScrollPosition',
  });

  const { loading, error, data, refetch, fetchMore, variables } = useQuery(
    INVESTOR_ACCREDITATION_APPLICATIONS_QUERY,
    {
      variables: {
        limit: 50,
        filters: {
          text: '',
        },
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  useEffect(() => {
    if (data?.investorAccreditationApplications.nodes.length) onDataLoad();
  }, [data, onDataLoad]);

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;
    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const filters = [
      {
        type: 'status',
        label: 'Application Status',
        readable: (value: CvlApplicationStatusEnumType) => cvlApplicationStatusToReadable(value),
        options: [
          CvlApplicationStatusEnumType.Registered,
          CvlApplicationStatusEnumType.ApplicationDraft,
          CvlApplicationStatusEnumType.ApplicationSubmitted,
          CvlApplicationStatusEnumType.ApplicationVerified,
          CvlApplicationStatusEnumType.ApplicationApproved,
          CvlApplicationStatusEnumType.ApplicationRejected,
        ].map(value => ({
          value,
          label: cvlApplicationStatusToReadable(value).label,
          color: `bg-${cvlApplicationStatusToReadable(value).color}-400`,
        })),
      },
      {
        type: 'paymentStatus',
        label: 'Payment Status',
        readable: (value: CvlPaymentStatusEnumType) => cvlPaymentStatusToReadable(value),
        options: [
          CvlPaymentStatusEnumType.Pending,
          CvlPaymentStatusEnumType.Success,
          CvlPaymentStatusEnumType.Failed,
        ].map(value => ({
          value,
          label: cvlPaymentStatusToReadable(value).label,
          color: `bg-${cvlPaymentStatusToReadable(value).color}-400`,
        })),
      },
    ];

    return (
      <>
        <FilterPanel
          onFilterChange={onReset}
          activeFilters={variables?.filters}
          filters={filters}
          refetch={refetch}
        />

        <div className="flex justify-between items-end mt-8">
          <TextField
            placeholder="Search by name, email or PAN"
            onChange={debounce(e => {
              refetch({
                filters: {
                  ...variables?.filters,
                  text: e.target.value,
                },
              });
            }, 500)}
          />
          <span className="text-sm text-gray-500">
            {data?.investorAccreditationApplications.nodes.length} of{' '}
            {data?.investorAccreditationApplications.pageInfo.totalCount} applications
          </span>
        </div>
        <DataTable
          data={data?.investorAccreditationApplications.nodes || []}
          onClick={application =>
            navigate(
              `/investment-entities/${application.investmentEntityId}/accreditation-application`
            )
          }
          onUnmount={onComponentUnmount}
          onScroll={onScroll}
          columns={[
            {
              label: 'Name',
              fieldName: 'name',
            },
            {
              label: 'PAN',
              fieldName: 'pan',
            },
            {
              label: 'Email',
              fieldName: 'email',
            },
            {
              label: 'Mobile',
              fieldName: 'mobile',
            },
            {
              label: 'Application Status',
              fieldName: 'status',
              type: 'CVL_APPLICATION_STATUS',
            },
            {
              label: 'Payment Status',
              fieldName: 'paymentStatus',
              type: 'CVL_PAYMENT_STATUS',
            },
            {
              label: 'Qualification Criteria',
              fieldName: 'financialDetails.qualificationCriteria',
              type: 'CVL_QUALIFICATION_CRITERIA',
            },
            {
              label: 'Applied On',
              fieldName: 'createdAt',
              type: 'DATE',
            },
          ]}
          hasNextPage={data?.investorAccreditationApplications.pageInfo.hasNextPage}
          paginationLoading={loading}
          onLoadMore={() =>
            fetchMore({
              variables: {
                cursor: data?.investorAccreditationApplications.pageInfo.cursor,
              },
            })
          }
        />
      </>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Accreditation Application"
        subTitle={`Monitor investor accreditation applications submitted via CVL${
          data
            ? ` (${data.investorAccreditationApplications.pageInfo?.totalCount || 0} applications)`
            : ''
        }`}
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default AccreditationApplication;
